export interface AmortizationRow {
    date: string;
    capital: number;
}

export interface AmortizationRowComputed extends AmortizationRow {
    interest: number;
}

export interface ParametricosDataForm {
    hectaresOverride: number | null;
    creditSurfaceHa: number;
    investmentConceptCt: string;
    investmentConceptRefa: string;
    program: string;
    fundingSource: string;
    promoter: string;
    contract: {
        classification: string;
        contractNumber: string;
        amount: number;
        openingDate: string;
        maturityDateOverride: string;
    };
    assetValue: number;
    financingAmountOverride: number | null;
    marginRate: number;
    amortizationRows: Array<AmortizationRow>;
    liabilities: {
        shortTerm: number;
        longTerm: number;
    };
    longTermAmortizations: [number, number, number, number, number];
    assets: {
        movable: number;
        immovable: number;
    };
}

/** Filas editables por año. La cantidad de años es variable — la define la longitud de estos arrays. */
export interface ParametricosProjectionForm {
    servicesIncome: Array<number>;
    supportIncome: Array<number>;
    rentCost: Array<number>;
    servicesCost: Array<number>;
    workingCapitalInterest: Array<number>;
    otherIncome: Array<number>;
    otherExpenses: Array<number>;
    interesCreditoSimple: Array<number>;
    interesCredito: Array<number>;
    notes: Array<string>;
}

/** Cantidad de años por defecto para una proyección nueva, sin datos guardados. */
export const PROJECTION_YEARS_COUNT = 6;

export const emptyYearArray = (): Array<number> => Array.from({ length: PROJECTION_YEARS_COUNT }, () => 0);

/** Ajusta un array de años a `count` elementos, rellenando con 0 o truncando. */
export const resizeYearArray = (values: Array<number>, count: number): Array<number> =>
    Array.from({ length: count }, (_, i) => values[i] ?? 0);

export const defaultDataForm: ParametricosDataForm = {
    hectaresOverride: null,
    creditSurfaceHa: 0,
    investmentConceptCt: "",
    investmentConceptRefa: "",
    program: "",
    fundingSource: "",
    promoter: "",
    contract: {
        classification: "",
        contractNumber: "",
        amount: 0,
        openingDate: "",
        maturityDateOverride: "",
    },
    assetValue: 0,
    financingAmountOverride: null,
    marginRate: 0,
    amortizationRows: [],
    liabilities: { shortTerm: 0, longTerm: 0 },
    longTermAmortizations: [0, 0, 0, 0, 0],
    assets: { movable: 0, immovable: 0 },
};

export const defaultProjectionForm: ParametricosProjectionForm = {
    servicesIncome: emptyYearArray(),
    supportIncome: emptyYearArray(),
    rentCost: emptyYearArray(),
    servicesCost: emptyYearArray(),
    workingCapitalInterest: emptyYearArray(),
    otherIncome: emptyYearArray(),
    otherExpenses: emptyYearArray(),
    interesCreditoSimple: emptyYearArray(),
    interesCredito: emptyYearArray(),
    notes: [],
};

/** Bajo `extra_conditions.parametric` de la `Condition` activa. */
export interface ParametricosExtraConditions {
    data?: Partial<ParametricosDataForm>;
    projection?: Partial<ParametricosProjectionForm>;
}
